import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { VirtualizedTable } from "../../components/common/VirtualizedTable";
import Layout from "../../components/UI/Layouts";
import Content from "../../components/UI/Layouts/Content";
import { ProgramCategory } from "../../types";
import { Chip, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ProgramCategoryModal from "../../components/ScreenCategories/ProgramCategoryModal";
import Popover from "@mui/material/Popover";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
import PageHeader from "../../components/common/PageHeader";
import { useStores } from "../../hooks/useStores";
import { observer } from "mobx-react";
import { useSnackbar } from "notistack";
import { Breadcrumb, BreadcrumbItem } from "../../components/UI/Breadcrumb";
import EmptySpace from "../../components/common/EmptySpace";
import Loading from "../../components/common/Loading";

const ProgramCategories: React.FC = observer(() => {
  const { t } = useTranslation();
  const { categoryStore } = useStores();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const modalHandler = () => {
    setIsModalOpen((prev) => !prev);
  };

  const [programCategory, setProgramCategory] = useState<ProgramCategory>();

  useEffect(() => {
    categoryStore.getProgramCategories();
  }, [categoryStore]);

  const handleSubmit = useCallback(
    async (data: ProgramCategory) => {
      data.visibleInApp === "visible"
        ? (data.visibleInApp = true)
        : (data.visibleInApp = false);

      try {
        await categoryStore.addProgramCategory(data);
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }

      modalHandler();
    },
    [categoryStore, enqueueSnackbar, t]
  );

  const handleUpdate = useCallback(
    async (data: ProgramCategory) => {
      data.visibleInApp === "visible"
        ? (data.visibleInApp = true)
        : (data.visibleInApp = false);

      try {
        await categoryStore.updateProgramCategory(data);
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
      setProgramCategory(undefined);
      modalHandler();
    },
    [categoryStore, enqueueSnackbar, t]
  );

  const handleProgramCategoriesOrderUpdate = useCallback(
    async (category: ProgramCategory, oldIndex: number, newIndex: number) => {
      try {
        await categoryStore.updateProgramCategoriesOrder(
          category,
          oldIndex,
          newIndex
        );

        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
      await categoryStore.getProgramCategories();
    },
    [categoryStore, enqueueSnackbar, t]
  );

  const handleDelete = useCallback(
    async (data: ProgramCategory) => {
      try {
        await categoryStore.deleteProgramCategory(data);
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
    },
    [categoryStore, enqueueSnackbar, t]
  );

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.programCategories.column.order"),
        dataKey: "orderNumber",
        numeric: true,
        sorter: (a: number, b: number) => (a < b ? -1 : a > b ? 1 : 0),
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.programCategories.column.name"),
        dataKey: "name",
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.programCategories.column.visible"),
        dataKey: "visibleInApp",
        render: (visibleInApp: boolean | string) => {
          return visibleInApp === true ? (
            <Chip
              label={t("screens.programCategories.column.visibleActive")}
              color="success"
            />
          ) : (
            <Chip
              label={t("screens.programCategories.column.visibleNotActive")}
              color="error"
            />
          );
        },
      },
      {
        width: 90,
        flexGrow: 0,
        label: t("common.action"),
        dataKey: "id",
        render: (id: number, category: ProgramCategory) => {
          return (
            <>
              <IconButton
                key={id}
                onClick={() => {
                  setProgramCategory(category);
                  modalHandler();
                }}
              >
                <EditIcon />
              </IconButton>

              <PopupState variant="popover" popupId="demo-popup-popover">
                {(popupState) => (
                  <div>
                    <IconButton color="error" {...bindTrigger(popupState)}>
                      <DeleteIcon />
                    </IconButton>
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                    >
                      <Typography sx={{ p: 1 }}>
                        {t("common.deletePopUp")}
                        <IconButton
                          color="error"
                          sx={{ p: 0 }}
                          onClick={() => handleDelete(category)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Typography>
                    </Popover>
                  </div>
                )}
              </PopupState>
            </>
          );
        },
      },
    ],
    [handleDelete, t]
  );

  return (
    <Layout hideFooter>
      <Content borderBottomWidth="1px">
        <Breadcrumb my={"20px"}>
          <BreadcrumbItem path="/">
            {t("screens.programCategories.breadcrumb.home")}
          </BreadcrumbItem>

          <BreadcrumbItem active>
            {t("screens.programCategories.breadcrumb.programCategories")}
          </BreadcrumbItem>
        </Breadcrumb>

        <PageHeader
          title={t("screens.programCategories.pageHeader.title")}
          buttonText={t("screens.programCategories.pageHeader.buttonText")}
          buttonEnable={true}
          handleButton={modalHandler}
        />
      </Content>
      {categoryStore.isLoading ? (
        <Loading />
      ) : categoryStore.programCategories.length > 0 ? (
        <Content fullHeight align="top" mt={[null, null, null, "0px"]}>
          <div
            style={{
              flex: "1 1 auto",
              width: "100%",
              height: `${(categoryStore.programCategories.length + 1) * 50}px`,
              display: "flex",
              marginTop: "20px",
            }}
          >
            <VirtualizedTable
              rowCount={categoryStore.programCategories.length}
              rowGetter={({ index }) => categoryStore.programCategories[index]}
              columns={columns}
              rowStyle={(row) =>
                row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
              }
              data={categoryStore.programCategories}
              onRowSort={handleProgramCategoriesOrderUpdate}
            />
          </div>
        </Content>
      ) : (
        <EmptySpace
          title={t("screens.programCategories.emptyData")}
          buttonEnable={true}
          buttonText={t("screens.programCategories.pageHeader.buttonText")}
          handleButton={modalHandler}
        />
      )}

      <ProgramCategoryModal
        category={programCategory}
        isSubmitting={false}
        isOpen={isModalOpen}
        defaultOrderNumber={categoryStore.programCategories.length + 1}
        onClose={() => {
          setProgramCategory(undefined);
          modalHandler();
        }}
        onSubmit={programCategory ? handleUpdate : handleSubmit}
      />
    </Layout>
  );
});

export default ProgramCategories;
